import { useRef } from 'react';

import type { UseFormReturnType } from '@mantine/form';
import { useForm } from '@mantine/form';

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import type { VaultPropertyColor } from '../../../../../../../shared/entities/VaultProperty';
import { VaultPropertyType } from '../../../../../../../shared/entities/VaultProperty';
import type { VaultPropertyOption } from '../../../../../../../shared/entities/VaultPropertyOption';
import {
  useCreateVaultPropertyMutation,
  useGetVaultPropertyQuery,
  useUpdateVaultPropertyMutation,
} from '../../../hooks/vaultProperty.hook';
import { getRandomPropertyColor } from '../utils/getRandomPropertyColor';

export type FormValues = {
  name: string;
  type: VaultPropertyType;
  options: FormVaultPropertyOption[];
  description: string;
  color: VaultPropertyColor;
};

export type FormVaultPropertyOption = VaultPropertyOption & {
  operation?: 'create' | 'update' | 'delete';
};

export function useVaultPropertyForm(vaultPropertyId?: number) {
  const { t } = useTranslation(['common']);
  const { data: vaultProperty } = useGetVaultPropertyQuery(vaultPropertyId);
  const newOptionId = useRef(-1);
  const generateNewOptionId = () => {
    newOptionId.current--;
    return newOptionId.current;
  };

  const form = useForm<FormValues>({
    initialValues: {
      name: vaultProperty?.name || '',
      type: vaultProperty?.type || VaultPropertyType.MULTISELECT,
      options: vaultProperty?.options || [
        { id: generateNewOptionId(), name: '', description: '' },
        { id: generateNewOptionId(), name: '', description: '' },
      ],
      description: vaultProperty?.description || '',
      color: vaultProperty?.color || getRandomPropertyColor(),
    },
    validate: {
      name: (value: string) => (value.length > 0 ? null : t('fieldRequired')),
      description: (value: string) =>
        value.length > 0 ? null : t('fieldRequired'),
      type: (value: VaultPropertyType) => (value ? null : t('fieldRequired')),
      options: (options: FormVaultPropertyOption[]) => {
        if (options.length < 2) {
          return t('fieldRequired');
        }

        const nonEmptyValues = options.filter(
          option =>
            option.name.trim() !== '' && option.description.trim() !== '',
        );

        return options.length === nonEmptyValues.length
          ? null
          : t('allFieldsRequired');
      },
    },
  });
  const { createVaultProperty, isCreating } = useCreateVaultProperty(form);
  const { updateVaultProperty, isUpdating } = useUpdateVaultProperty(
    form,
    vaultPropertyId,
  );

  const addOptionInput = () => {
    form.insertListItem('options', {
      id: generateNewOptionId(),
      name: '',
      description: '',
      operation: 'create',
    });
  };

  const removeOption = (id: number) => {
    const index = form.values.options.findIndex(option => option.id === id);

    form.setFieldValue(`options.${index}.operation`, 'delete');
  };

  const markOptionAsUpdated = (index: number) => {
    if (form.values.options[index].operation !== 'create') {
      form.setFieldValue(`options.${index}.operation`, 'update');
    }
  };

  return {
    form,
    createVaultProperty: form.onSubmit(createVaultProperty),
    updateVaultProperty: (withTagsInference?: boolean) =>
      form.onSubmit((formValues: FormValues) =>
        updateVaultProperty({ ...formValues }, withTagsInference),
      ),
    isSubmitting: isCreating || isUpdating,
    addOptionInput,
    removeOption,
    markOptionAsUpdated,
  };
}

const useCreateVaultProperty = (form: UseFormReturnType<FormValues>) => {
  const { t } = useTranslation(['bidResponse']);
  const createVaultPropertyMutation = useCreateVaultPropertyMutation();

  const createVaultProperty = async (values: FormValues) => {
    if (
      validateDuplicateOptions(
        values.options.filter(option => option.operation !== 'delete'),
        form,
        t,
      )
    ) {
      form.setFieldError(
        'options',
        t('vault.sidepanels.createProperty.input.values.duplicate'),
      );
      return;
    }

    const createdOptions = values.options.map(option => {
      const { id: _id, operation: _operation, ...newOption } = option;
      return newOption;
    });

    createVaultPropertyMutation.mutate({
      ...values,
      options: createdOptions,
    });
  };
  return {
    createVaultProperty,
    isCreating: createVaultPropertyMutation.isPending,
  };
};

const useUpdateVaultProperty = (
  form: UseFormReturnType<FormValues>,
  vaultPropertyId: number | undefined,
) => {
  const { t } = useTranslation(['bidResponse']);
  const { mutate: updateVaultPropertyMutation, isPending } =
    useUpdateVaultPropertyMutation();

  const updateVaultProperty = async (
    values: FormValues,
    withTagsInference = false,
  ) => {
    if (!vaultPropertyId) {
      throw new Error('Vault property ID is required');
    }

    if (
      validateDuplicateOptions(
        values.options.filter(option => option.operation !== 'delete'),
        form,
        t,
      )
    ) {
      return;
    }

    const deletedOptions = values.options.filter(
      option => option.operation === 'delete',
    ) as VaultPropertyOption[];

    const updatedOptions = values.options.filter(
      option => option.operation === 'update',
    ) as VaultPropertyOption[];

    const createdOptions = values.options
      .filter(option => option.operation === 'create')
      .map(option => {
        const { id: _id, operation: _operation, ...newOption } = option;
        return newOption;
      });

    updateVaultPropertyMutation({
      payload: {
        ...values,
        options: {
          create: createdOptions,
          update: updatedOptions,
          delete: deletedOptions.map(option => option.id),
        },
      },
      vaultPropertyId: vaultPropertyId,
      withTagsInference,
    });
  };

  return {
    updateVaultProperty,
    isUpdating: isPending,
  };
};

const validateDuplicateOptions = (
  options: FormVaultPropertyOption[],
  form: UseFormReturnType<FormValues>,
  t: TFunction<['bidResponse']>,
): boolean => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const option of options) {
    const name = option.name.trim();
    if (!name) continue;
    if (seen.has(name)) {
      duplicates.add(name);
    }
    seen.add(name);
  }

  if (duplicates.size > 0) {
    for (const [index, option] of options.entries()) {
      if (duplicates.has(option.name.trim())) {
        form.setFieldError(
          `values.${index}.name`,
          t('vault.sidepanels.createProperty.input.values.duplicate' as const),
        );
      }
    }
    return true;
  }

  return false;
};
